// WF-POLISH (2026-10-27): the Overview leads with what the DE is and does (de-about.tsx: role,
// goal, backstory, responsibilities, KPIs, open tasks, recent work); readiness shows only while
// the DE is not live, and the runtime is one line at the bottom of the page (de-profile.tsx).
// DE profile › Overview: the setup checklist while the DE is in setup, then its readiness at a
// glance (DE-WIZARD's Draft → Awaiting approval → Deploying → Live strip with the reason, and
// "Finish set-up" / "Edit set-up"; status, LiteLLM key, ingest keys) with links to the tabs that change them. Reads the
// profile only, so the tab adds no API call — except, for managers of a deployed DE with the
// `agents` add-on, its drift audits (DC-10: one call), and for managers with the `memory` add-on
// its Recall memory agent (M7-05: one call). Phase 2: for a DE with an AiGentPanel binding, the
// Hermes execution section (hermes-execution.tsx) from the profile alone (not for a
// self-hosted DE). WF-FIX: the agents (AG) reporting to it (the cached agents list) and the
// channels it is in, with Add to channel.
import { Button, Notice, SettingsSection } from "@companyos/ui";
import { ActivityIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import { SkillChips } from "../agents/helpers";
import { type CompanyAccess, isManager } from "../company";
import { formatDateTime } from "../format";
import { Facts } from "../screen-parts";
import { useFeature } from "../use-feature";
import { DeChannelsSection } from "./channels-section";
import { DeAbout } from "./de-about";
import type { DeTab } from "./de-profile";
import { DeProvisioningSection } from "./de-provisioning";
import { SetupChecklist } from "./de-setup";
import { DE_STATUS_LABELS } from "./de-status";
import { DeWebhooksSection } from "./de-webhooks";
import { DriftAuditsSection } from "./drift-audits";
import { HermesExecutionSection } from "./hermes-execution";
import { liveness } from "./liveness";
import { ReadinessStrip } from "./readiness";
import { RecallAgentSection } from "./recall-agent";
import { DeReportingAgents } from "./reporting-agents";

const STATUS_TEXT: Record<DeProfile["status"], string> = {
  onboarding: "Not live yet: finish the steps above.",
  // WF-FIX: active is not Live; Live is earned by activity (the description says which).
  active: "Active: its keys are accepted and its work is counted.",
  paused: "Paused: its keys are refused until it is resumed.",
  retired: "Retired: off its seat, keys revoked. Its history is kept.",
  not_deployed: "Not deployed: on the chart, no runtime yet. It runs nothing.",
};

export function OverviewTab({
  api,
  company,
  de,
  kpi,
  onTab,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
  kpi: boolean;
  onTab: (tab: DeTab) => void;
}) {
  const manager = isManager(company);
  const agents = useFeature("agents");
  const memory = useFeature("memory");
  const liveKeys = de.keys.filter((k) => k.status === "active");
  const lastUse = liveKeys
    .map((k) => k.last_used_at)
    .filter((t): t is string => Boolean(t))
    .sort()
    .at(-1);
  // WF-POLISH: readiness only while there is something to do about it (a live or idle active DE
  // needs no strip; its keys and deploy are the Runtime line's).
  const settled = de.status === "active" && de.readiness.state === "live";
  return (
    <div className="flex min-w-0 flex-col gap-8" data-slot="de-overview">
      {de.status === "onboarding" ? <SetupChecklist api={api} company={company} de={de} /> : null}
      {de.status === "paused" && de.pause_reason === "wallet" ? (
        <Notice tone="warning">
          {de.display_name} is paused · no credit. It resumes when funds are added in Setup ›
          Billing.
        </Notice>
      ) : de.status === "paused" ? (
        <Notice tone="warning">
          {de.display_name} is paused. Resume it from the page header to accept its keys again.
        </Notice>
      ) : null}
      {/* WF-POLISH: what it is and does leads the page. */}
      <DeAbout api={api} company={company} de={de} kpi={kpi} />
      {/* HELPERS-2 (Codex HELPERS): the DE's own skills here too, not only on its Helpers tab. */}
      {de.role_skill_keys.length ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2" data-slot="de-overview-skills">
          <span className="text-xs text-muted-foreground">Skills</span>
          <SkillChips skills={de.role_skill_keys} company={company} />
        </div>
      ) : null}
      {/* PROVISION-IN-PRODUCT: what the product set up on create (managers). */}
      <DeProvisioningSection api={api} company={company} de={de} />
      {settled ? null : (
        <SettingsSection
          icon={ActivityIcon}
          title="Readiness"
          description={(() => {
            const l = liveness(de);
            return l ? `${l.label}: ${l.reason}.` : STATUS_TEXT[de.status];
          })()}
          actions={
            manager ? (
              <>
                {/* DE-WIZARD: a draft is finished (submitted or deployed) from Setup › Deploy. */}
                <Button
                  variant={de.readiness.state === "draft" ? "default" : "secondary"}
                  size="sm"
                  onClick={() => onTab(de.readiness.state === "draft" ? "deploy" : "job")}
                  data-slot="edit-setup"
                >
                  {de.readiness.state === "draft" ? "Finish set-up" : "Edit set-up"}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => onTab("keys")}>
                  Manage keys
                </Button>
              </>
            ) : null
          }
        >
          <ReadinessStrip readiness={de.readiness} liveLabel={liveness(de)?.label} />
          <Facts
            items={[
              { label: "Status", value: DE_STATUS_LABELS[de.status] },
              {
                label: "LiteLLM key",
                value: de.litellm_key_alias ? (
                  <code className="font-mono text-xs">{de.litellm_key_alias}</code>
                ) : (
                  "Not issued"
                ),
              },
              ...(manager
                ? [
                    {
                      label: "Ingest keys",
                      value: liveKeys.length
                        ? `${liveKeys.length} active${lastUse ? ` · last used ${formatDateTime(lastUse)}` : " · never used"}`
                        : "None active",
                    },
                  ]
                : []),
            ]}
          />
        </SettingsSection>
      )}
      {manager && agents.enabled && (de.deploy_ref || de.template_version_id) ? (
        <DriftAuditsSection
          api={api}
          companyId={company.id}
          deId={de.membership_id}
          deployed={Boolean(de.template_version_id)}
        />
      ) : null}
      {manager && memory.enabled ? (
        <RecallAgentSection api={api} co={company.id} deId={de.membership_id} />
      ) : null}
      {/* WF-FIX: the agents (AG) that report to it. */}
      <DeReportingAgents api={api} company={company} de={de} />
      {/* WF-FIX (UX pass P0 0.7): the channels it is in, with Add to channel. */}
      <DeChannelsSection api={api} company={company} de={de} />
      {/* CHAT-SWITCH-B: incoming webhooks that wake it (company admins). */}
      <DeWebhooksSection api={api} company={company} de={de} />
      {/* Phase 2: the Hermes container, reconciliation, routing evidence and the opt-in (F7);
          not for a self-hosted DE, whose Hermes runs outside AiGentPanel (WF-FIX). */}
      {de.aigentpanel?.execution && !de.self_hosted ? (
        <HermesExecutionSection api={api} companyId={company.id} de={de} manager={manager} />
      ) : null}
      {/* WF-POLISH: the week's KPIs are in "What it is" above; the scorecard is the KPIs tab. */}
    </div>
  );
}
